import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Code2,
  Users,
  MessageSquare,
  UserPlus,
  Pencil,
  Trash2,
  Loader2,
  LogOut,
  Webhook,
  Eye,
  EyeOff,
  Copy,
  Check,
  Wifi,
  WifiOff,
  Clock,
  Calendar,
  Play,
  Square,
  Link2,
  ExternalLink
} from 'lucide-react';
import { getAdmins, createAdmin, updateAdmin, deleteAdmin, getDeveloperStats, getAllInstances, deleteInstanceAsDeveloper, logout, activateTrial, deactivateTrial } from '../lib/api';
import { toast } from 'sonner';

const DeveloperDashboard = ({ user, onLogout }) => {
  const [admins, setAdmins] = useState([]);
  const [instances, setInstances] = useState([]);
  const [stats, setStats] = useState({ total_admins: 0, total_attendants: 0, total_conversations: 0, total_instances: 0 });
  const [loading, setLoading] = useState(true);
  const [trialDays, setTrialDays] = useState('7');
  const [trialDialogOpen, setTrialDialogOpen] = useState(false);
  const [selectedAdminForTrial, setSelectedAdminForTrial] = useState(null);
  const [activeTab, setActiveTab] = useState('admins'); // 'admins' or 'instances'
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showWebhooksDialog, setShowWebhooksDialog] = useState(false);
  const [showEvolutionDialog, setShowEvolutionDialog] = useState(false);
  const [showApiDialog, setShowApiDialog] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    cnpj: '',
    nome_fantasia: '',
    telefone: '',
    endereco_logradouro: '',
    endereco_numero: '',
    endereco_complemento: '',
    endereco_bairro: '',
    endereco_cidade: '',
    endereco_estado: '',
    endereco_cep: '',
    email: '',
    password: '',
    max_instances: 5
  });
  const [webhookData, setWebhookData] = useState({ webhook_url_incoming: '', webhook_url_outgoing: '' });
  const [evolutionData, setEvolutionData] = useState({ evolution_api_key: '', evolution_url: '', evolution_webhook: '' });
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showEvolutionApiKey, setShowEvolutionApiKey] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  // Base URL for public API
  const getApiBaseUrl = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    return backendUrl;
  };

  const getPublicApiUrl = (admin) => {
    if (!admin?.webhook_token) return '';
    return `${getApiBaseUrl()}/api/public/admin/${admin.webhook_token}`;
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [adminsRes, statsRes, instancesRes] = await Promise.all([
        getAdmins(),
        getDeveloperStats(),
        getAllInstances()
      ]);
      setAdmins(adminsRes.data);
      setStats(statsRes.data);
      setInstances(instancesRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async () => {
    if (
      !formData.name ||
      !formData.cnpj ||
      !formData.nome_fantasia ||
      !formData.telefone ||
      !formData.endereco_logradouro ||
      !formData.endereco_numero ||
      !formData.endereco_bairro ||
      !formData.endereco_cidade ||
      !formData.endereco_estado ||
      !formData.endereco_cep ||
      !formData.email ||
      !formData.password
    ) {
      toast.error('Preencha todos os dados da empresa');
      return;
    }

    setSaving(true);
    try {
      await createAdmin({
        name: formData.name,
        cnpj: formData.cnpj,
        nome_fantasia: formData.nome_fantasia,
        telefone: formData.telefone,
        endereco_logradouro: formData.endereco_logradouro,
        endereco_numero: formData.endereco_numero,
        endereco_complemento: formData.endereco_complemento || null,
        endereco_bairro: formData.endereco_bairro,
        endereco_cidade: formData.endereco_cidade,
        endereco_estado: formData.endereco_estado,
        endereco_cep: formData.endereco_cep,
        email: formData.email,
        password: formData.password,
        max_instances: formData.max_instances || 5
      });
      toast.success('Empresa cadastrada com sucesso!');
      setShowCreateDialog(false);
      setFormData({
        name: '',
        cnpj: '',
        nome_fantasia: '',
        telefone: '',
        endereco_logradouro: '',
        endereco_numero: '',
        endereco_complemento: '',
        endereco_bairro: '',
        endereco_cidade: '',
        endereco_estado: '',
        endereco_cep: '',
        email: '',
        password: '',
        max_instances: 5
      });
      loadData();
    } catch (error) {
      console.error('Error creating admin:', error);
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar empresa');
    } finally {
      setSaving(false);
    }
  };

  const handleEditAdmin = (admin) => {
    setSelectedAdmin(admin);
    setFormData({ 
      name: admin.name, 
      cnpj: admin.cnpj || '',
      nome_fantasia: admin.nome_fantasia || '',
      telefone: admin.telefone || '',
      endereco_logradouro: admin.endereco_logradouro || '',
      endereco_numero: admin.endereco_numero || '',
      endereco_complemento: admin.endereco_complemento || '',
      endereco_bairro: admin.endereco_bairro || '',
      endereco_cidade: admin.endereco_cidade || '',
      endereco_estado: admin.endereco_estado || '',
      endereco_cep: admin.endereco_cep || '',
      email: admin.email, 
      password: '',
      max_instances: admin.max_instances || 5,
      active: admin.active !== false
    });
    setShowEditDialog(true);
  };

  const handleUpdateAdmin = async () => {
    if (
      !formData.name ||
      !formData.cnpj ||
      !formData.nome_fantasia ||
      !formData.telefone ||
      !formData.endereco_logradouro ||
      !formData.endereco_numero ||
      !formData.endereco_bairro ||
      !formData.endereco_cidade ||
      !formData.endereco_estado ||
      !formData.endereco_cep ||
      !formData.email
    ) {
      toast.error('Preencha todos os dados da empresa');
      return;
    }

    setSaving(true);
    try {
      const updateData = { 
        name: formData.name, 
        cnpj: formData.cnpj,
        nome_fantasia: formData.nome_fantasia,
        telefone: formData.telefone,
        endereco_logradouro: formData.endereco_logradouro,
        endereco_numero: formData.endereco_numero,
        endereco_complemento: formData.endereco_complemento || null,
        endereco_bairro: formData.endereco_bairro,
        endereco_cidade: formData.endereco_cidade,
        endereco_estado: formData.endereco_estado,
        endereco_cep: formData.endereco_cep,
        email: formData.email,
        max_instances: formData.max_instances,
        active: formData.active
      };
      if (formData.password) {
        updateData.password = formData.password;
      }
      await updateAdmin(selectedAdmin.id, updateData);
      toast.success('Empresa atualizada!');
      setShowEditDialog(false);
      setSelectedAdmin(null);
      setFormData({
        name: '',
        cnpj: '',
        nome_fantasia: '',
        telefone: '',
        endereco_logradouro: '',
        endereco_numero: '',
        endereco_complemento: '',
        endereco_bairro: '',
        endereco_cidade: '',
        endereco_estado: '',
        endereco_cep: '',
        email: '',
        password: '',
        max_instances: 5
      });
      loadData();
    } catch (error) {
      console.error('Error updating admin:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenTrialDialog = (admin) => {
    setSelectedAdminForTrial(admin);
    setTrialDays('7');
    setTrialDialogOpen(true);
  };

  const handleActivateTrial = async () => {
    if (!selectedAdminForTrial) return;
    
    try {
      await activateTrial(selectedAdminForTrial.id, parseInt(trialDays));
      toast.success(`Período de teste de ${trialDays} dias ativado!`);
      setTrialDialogOpen(false);
      setSelectedAdminForTrial(null);
      loadData();
    } catch (error) {
      console.error('Error activating trial:', error);
      toast.error('Erro ao ativar período de teste');
    }
  };

  const handleDeactivateTrial = async (adminId) => {
    if (!window.confirm('Tem certeza que deseja desativar o período de teste?')) {
      return;
    }
    
    try {
      await deactivateTrial(adminId);
      toast.success('Período de teste desativado');
      loadData();
    } catch (error) {
      console.error('Error deactivating trial:', error);
      toast.error('Erro ao desativar período de teste');
    }
  };

  const getTrialStatus = (admin) => {
    if (!admin.trial_enabled || !admin.trial_end_date) {
      return null;
    }
    
    const endDate = new Date(admin.trial_end_date);
    const now = new Date();
    const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft <= 0) {
      return { status: 'expired', daysLeft: 0, endDate };
    }
    
    return { status: 'active', daysLeft, endDate };
  };

  const handleDeleteAdmin = async (admin) => {
    if (!window.confirm(`Tem certeza que deseja excluir "${admin.name}"? Todos os dados associados serão perdidos.`)) {
      return;
    }

    try {
      await deleteAdmin(admin.id);
      toast.success('Administrador excluído!');
      loadData();
    } catch (error) {
      console.error('Error deleting admin:', error);
      toast.error('Erro ao excluir');
    }
  };

  const handleToggleActive = async (admin) => {
    try {
      await updateAdmin(admin.id, { active: !admin.active });
      toast.success(admin.active ? 'Conta desativada' : 'Conta ativada');
      loadData();
    } catch (error) {
      console.error('Error toggling admin status:', error);
      toast.error('Erro ao alterar status');
    }
  };

  const handleUpdateAccessType = async (adminId, accessType) => {
    try {
      await updateAdmin(adminId, { access_type: accessType });
      toast.success('Tipo de acesso atualizado');
      loadData();
    } catch (error) {
      console.error('Error updating access type:', error);
      toast.error('Erro ao atualizar tipo de acesso');
    }
  };

  const handleWebhooks = (admin) => {
    setSelectedAdmin(admin);
    setWebhookData({
      webhook_url_outgoing: admin.webhook_url_outgoing || '',
      webhook_url_internal: admin.webhook_url_internal || ''
    });
    setShowWebhooksDialog(true);
  };

  const handleEvolution = (admin) => {
    setSelectedAdmin(admin);
    setEvolutionData({
      evolution_api_key: admin.evolution_api_key || '',
      evolution_url: admin.evolution_url || '',
      evolution_webhook: admin.evolution_webhook || ''
    });
    setShowEvolutionApiKey(false);
    setShowEvolutionDialog(true);
  };

  const handleShowApi = (admin) => {
    setSelectedAdmin(admin);
    setShowApiDialog(true);
  };

  const handleSaveWebhooks = async () => {
    setSaving(true);
    try {
      await updateAdmin(selectedAdmin.id, { 
        webhook_url_outgoing: webhookData.webhook_url_outgoing,
        webhook_url_internal: webhookData.webhook_url_internal
      });
      toast.success('Webhooks atualizados!');
      setShowWebhooksDialog(false);
      loadData();
    } catch (error) {
      console.error('Error saving webhooks:', error);
      toast.error('Erro ao salvar webhooks');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEvolution = async () => {
    setSaving(true);
    try {
      await updateAdmin(selectedAdmin.id, {
        evolution_api_key: evolutionData.evolution_api_key || null,
        evolution_url: evolutionData.evolution_url || null,
        evolution_webhook: evolutionData.evolution_webhook || null
      });
      toast.success('Evolution atualizado!');
      setShowEvolutionDialog(false);
      loadData();
    } catch (error) {
      console.error('Error saving evolution settings:', error);
      toast.error('Erro ao salvar Evolution');
    } finally {
      setSaving(false);
    }
  };

  // Gera a URL do webhook de recebimento baseado no token
  const getWebhookIncomingUrl = (admin) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `${baseUrl}/api/webhooks/incoming/${admin.webhook_token}`;
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    onLogout();
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
      toast.success('Copiado!');
    } catch (error) {
      toast.error('Erro ao copiar');
    }
  };

  const handleDeleteInstance = async (instance) => {
    if (!window.confirm(`Tem certeza que deseja excluir a instância "${instance.name}"?`)) {
      return;
    }

    try {
      await deleteInstanceAsDeveloper(instance.id);
      toast.success('Instância excluída!');
      loadData();
    } catch (error) {
      console.error('Error deleting instance:', error);
      toast.error('Erro ao excluir instância');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/logo.png" 
              alt="integra+" 
              className="w-10 h-10 object-contain"
            />
            <div>
              <h1 className="text-xl font-bold">Painel do Desenvolvedor</h1>
              <p className="text-sm text-muted-foreground">Bem-vindo, {user.username}</p>
            </div>
          </div>
          <Button variant="ghost" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Sair
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-500/10 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_admins}</p>
                  <p className="text-sm text-muted-foreground">Administradores</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_attendants}</p>
                  <p className="text-sm text-muted-foreground">Atendentes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-500/10 rounded-full flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_conversations}</p>
                  <p className="text-sm text-muted-foreground">Conversas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-500/10 rounded-full flex items-center justify-center">
                  <Wifi className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_instances}</p>
                  <p className="text-sm text-muted-foreground">Instâncias WhatsApp</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'admins' ? 'default' : 'outline'}
            onClick={() => setActiveTab('admins')}
          >
            <Users className="h-4 w-4 mr-2" />
            Administradores
          </Button>
          <Button
            variant={activeTab === 'instances' ? 'default' : 'outline'}
            onClick={() => setActiveTab('instances')}
          >
            <Wifi className="h-4 w-4 mr-2" />
            Instâncias WhatsApp
          </Button>
        </div>

        {/* Admins Table */}
        {activeTab === 'admins' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Administradores</CardTitle>
              <CardDescription>Gerencie os administradores do sistema</CardDescription>
            </div>
            <Button onClick={() => setShowCreateDialog(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Cadastrar Empresa
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Acesso</TableHead>
                  <TableHead>Teste</TableHead>
                  <TableHead>Instâncias</TableHead>
                  <TableHead>Webhooks</TableHead>
                  <TableHead>Evolution</TableHead>
                  <TableHead>Criado em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                      Nenhum administrador cadastrado
                    </TableCell>
                  </TableRow>
                ) : (
                  admins.map((admin) => {
                    const trialStatus = getTrialStatus(admin);
                    const accessLabel = {
                      'app': 'Só App',
                      'platform': 'Só Plataforma',
                      'both': 'App + Plataforma'
                    };
                    return (
                    <TableRow key={admin.id} className={!admin.active ? 'opacity-50' : ''}>
                      <TableCell className="font-medium">{admin.name}</TableCell>
                      <TableCell>{admin.email}</TableCell>
                      <TableCell>
                        <Badge 
                          variant={admin.active !== false ? "default" : "destructive"}
                          className="cursor-pointer"
                          onClick={() => handleToggleActive(admin)}
                          title={admin.active !== false ? "Clique para desativar" : "Clique para ativar"}
                        >
                          {admin.active !== false ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={admin.access_type || 'both'} 
                          onValueChange={(value) => handleUpdateAccessType(admin.id, value)}
                        >
                          <SelectTrigger className="w-[140px] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="app">Só App</SelectItem>
                            <SelectItem value="platform">Só Plataforma</SelectItem>
                            <SelectItem value="both">App + Plataforma</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {trialStatus ? (
                          <Badge 
                            variant={trialStatus.status === 'active' ? "default" : "destructive"}
                            className="flex items-center gap-1 w-fit"
                          >
                            <Clock className="h-3 w-3" />
                            {trialStatus.status === 'active' 
                              ? `${trialStatus.daysLeft} dias restantes`
                              : 'Expirado'}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Sem teste</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          máx: {admin.max_instances || 5}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={admin.webhook_url_outgoing ? "outline" : "secondary"}>
                          {admin.webhook_url_outgoing ? 'Configurado' : 'Não configurado'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={(admin.evolution_url || admin.evolution_api_key || admin.evolution_webhook) ? "outline" : "secondary"}>
                          {(admin.evolution_url || admin.evolution_api_key || admin.evolution_webhook) ? 'Configurado' : 'Não configurado'}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(admin.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {trialStatus?.status === 'active' ? (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleDeactivateTrial(admin.id)}
                              title="Encerrar teste"
                              className="text-orange-500 hover:text-orange-600"
                            >
                              <Square className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleOpenTrialDialog(admin)}
                              title="Ativar teste"
                              className="text-green-500 hover:text-green-600"
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleShowApi(admin)}
                            title="Link de API"
                            className="text-blue-500 hover:text-blue-600"
                          >
                            <Link2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleWebhooks(admin)}
                            title="Configurar Webhooks"
                          >
                            <Webhook className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleEvolution(admin)}
                            title="Configurar Evolution"
                          >
                            <Code2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleEditAdmin(admin)}
                            title="Editar"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDeleteAdmin(admin)}
                            title="Excluir"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )})
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        )}

        {/* Instances Table */}
        {activeTab === 'instances' && (
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Instâncias WhatsApp</CardTitle>
              <CardDescription>Visualize e gerencie todas as instâncias de todos os administradores</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Admin</TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>API Endpoint</TableHead>
                  <TableHead>Criado em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {instances.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      Nenhuma instância cadastrada
                    </TableCell>
                  </TableRow>
                ) : (
                  instances.map((instance) => (
                    <TableRow key={instance.id}>
                      <TableCell className="font-medium">{instance.name}</TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-sm">{instance.admin_name || '-'}</span>
                          <span className="text-xs text-muted-foreground">{instance.admin_email}</span>
                        </div>
                      </TableCell>
                      <TableCell>{instance.phone_number || '-'}</TableCell>
                      <TableCell>
                        <Badge 
                          variant={instance.status === 'connected' ? "default" : "secondary"}
                          className="flex items-center gap-1 w-fit"
                        >
                          {instance.status === 'connected' ? (
                            <Wifi className="h-3 w-3" />
                          ) : (
                            <WifiOff className="h-3 w-3" />
                          )}
                          {instance.status === 'connected' ? 'Conectado' : 
                           instance.status === 'disconnected' ? 'Desconectado' : 
                           instance.status === 'requires_reconnection' ? 'Reconectar' : instance.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground font-mono truncate max-w-[200px] block">
                          {instance.api_endpoint}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(instance.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDeleteInstance(instance)}
                          title="Excluir"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        )}
      </main>

      {/* Create Admin Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Cadastrar Empresa</DialogTitle>
            <DialogDescription>
              Cadastre uma empresa e defina os dados de acesso do administrador.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[65vh] pr-2">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nome (Razão Social)</Label>
                <Input
                  placeholder="Razão social"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>CNPJ</Label>
                <Input
                  placeholder="00.000.000/0000-00"
                  value={formData.cnpj}
                  onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Nome Fantasia</Label>
                <Input
                  placeholder="Nome fantasia"
                  value={formData.nome_fantasia}
                  onChange={(e) => setFormData({ ...formData, nome_fantasia: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input
                  type="tel"
                  placeholder="(00) 00000-0000"
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Endereço - Logradouro</Label>
                <Input
                  placeholder="Rua, Avenida, etc."
                  value={formData.endereco_logradouro}
                  onChange={(e) => setFormData({ ...formData, endereco_logradouro: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Número</Label>
                  <Input
                    placeholder="Número"
                    value={formData.endereco_numero}
                    onChange={(e) => setFormData({ ...formData, endereco_numero: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Complemento</Label>
                  <Input
                    placeholder="Complemento"
                    value={formData.endereco_complemento}
                    onChange={(e) => setFormData({ ...formData, endereco_complemento: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Bairro</Label>
                  <Input
                    placeholder="Bairro"
                    value={formData.endereco_bairro}
                    onChange={(e) => setFormData({ ...formData, endereco_bairro: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cidade</Label>
                  <Input
                    placeholder="Cidade"
                    value={formData.endereco_cidade}
                    onChange={(e) => setFormData({ ...formData, endereco_cidade: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Input
                    placeholder="UF"
                    value={formData.endereco_estado}
                    onChange={(e) => setFormData({ ...formData, endereco_estado: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>CEP</Label>
                  <Input
                    placeholder="00000-000"
                    value={formData.endereco_cep}
                    onChange={(e) => setFormData({ ...formData, endereco_cep: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="email@exemplo.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Senha</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Senha de acesso"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="absolute right-0 top-0 h-full"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Limite de Instâncias WhatsApp</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  placeholder="5"
                  value={formData.max_instances}
                  onChange={(e) => setFormData({ ...formData, max_instances: parseInt(e.target.value) || 5 })}
                />
                <p className="text-xs text-muted-foreground">
                  Número máximo de instâncias WhatsApp que este admin pode criar
                </p>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateAdmin} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Cadastrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Admin Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Editar Empresa</DialogTitle>
            <DialogDescription>
              Atualize os dados da empresa. Deixe a senha em branco para manter a atual.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[65vh] pr-2">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nome (Razão Social)</Label>
                <Input
                  placeholder="Razão social"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>CNPJ</Label>
                <Input
                  placeholder="00.000.000/0000-00"
                  value={formData.cnpj}
                  onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Nome Fantasia</Label>
                <Input
                  placeholder="Nome fantasia"
                  value={formData.nome_fantasia}
                  onChange={(e) => setFormData({ ...formData, nome_fantasia: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input
                  type="tel"
                  placeholder="(00) 00000-0000"
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Endereço - Logradouro</Label>
                <Input
                  placeholder="Rua, Avenida, etc."
                  value={formData.endereco_logradouro}
                  onChange={(e) => setFormData({ ...formData, endereco_logradouro: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Número</Label>
                  <Input
                    placeholder="Número"
                    value={formData.endereco_numero}
                    onChange={(e) => setFormData({ ...formData, endereco_numero: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Complemento</Label>
                  <Input
                    placeholder="Complemento"
                    value={formData.endereco_complemento}
                    onChange={(e) => setFormData({ ...formData, endereco_complemento: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Bairro</Label>
                  <Input
                    placeholder="Bairro"
                    value={formData.endereco_bairro}
                    onChange={(e) => setFormData({ ...formData, endereco_bairro: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cidade</Label>
                  <Input
                    placeholder="Cidade"
                    value={formData.endereco_cidade}
                    onChange={(e) => setFormData({ ...formData, endereco_cidade: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Input
                    placeholder="UF"
                    value={formData.endereco_estado}
                    onChange={(e) => setFormData({ ...formData, endereco_estado: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>CEP</Label>
                  <Input
                    placeholder="00000-000"
                    value={formData.endereco_cep}
                    onChange={(e) => setFormData({ ...formData, endereco_cep: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="email@exemplo.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Nova Senha (opcional)</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Deixe em branco para manter"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="absolute right-0 top-0 h-full"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Limite de Instâncias WhatsApp</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.max_instances}
                  onChange={(e) => setFormData({ ...formData, max_instances: parseInt(e.target.value) || 5 })}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                <div>
                  <Label>Status da Conta</Label>
                  <p className="text-xs text-muted-foreground">
                    {formData.active ? 'Conta ativa - pode fazer login' : 'Conta desativada - não pode fazer login'}
                  </p>
                </div>
                <Switch
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                />
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateAdmin} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Webhooks Dialog */}
      <Dialog open={showWebhooksDialog} onOpenChange={setShowWebhooksDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Configurar Webhooks</DialogTitle>
            <DialogDescription>
              Configure os webhooks para {selectedAdmin?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Webhook de Recebimento - Gerado automaticamente */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                Webhook de Recebimento (Incoming)
                <Badge variant="secondary" className="text-xs">Gerado automaticamente</Badge>
              </Label>
              <p className="text-xs text-muted-foreground">
                Configure esta URL na sua API do WhatsApp para receber mensagens nesta plataforma
              </p>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={selectedAdmin ? getWebhookIncomingUrl(selectedAdmin) : ''}
                  className="font-mono text-sm bg-muted"
                />
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => copyToClipboard(getWebhookIncomingUrl(selectedAdmin), 'incoming')}
                  title="Copiar URL"
                >
                  {copiedField === 'incoming' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            
            <Separator />
            
            {/* Webhook de Envio - Configurável */}
            <div className="space-y-2">
              <Label>Webhook de Envio (Outgoing)</Label>
              <p className="text-xs text-muted-foreground">
                URL para onde as mensagens dos atendentes serão enviadas (sua API do WhatsApp)
              </p>
              <div className="flex gap-2">
                <Input
                  placeholder="https://sua-api-whatsapp.com/send"
                  value={webhookData.webhook_url_outgoing}
                  onChange={(e) => setWebhookData({ ...webhookData, webhook_url_outgoing: e.target.value })}
                />
                {webhookData.webhook_url_outgoing && (
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => copyToClipboard(webhookData.webhook_url_outgoing, 'outgoing')}
                  >
                    {copiedField === 'outgoing' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                )}
              </div>
            </div>
            
            <Separator />
            
            {/* Webhook Interno - Para notificações */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                Webhook Interno (Notificações)
                <Badge variant="outline" className="text-xs">Opcional</Badge>
              </Label>
              <p className="text-xs text-muted-foreground">
                URL para receber notificações internas (novas mensagens, fora do horário, etc.)
              </p>
              <div className="flex gap-2">
                <Input
                  placeholder="https://seu-sistema.com/webhook/notificacoes"
                  value={webhookData.webhook_url_internal}
                  onChange={(e) => setWebhookData({ ...webhookData, webhook_url_internal: e.target.value })}
                />
                {webhookData.webhook_url_internal && (
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => copyToClipboard(webhookData.webhook_url_internal, 'internal')}
                  >
                    {copiedField === 'internal' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Eventos enviados: <code className="bg-muted px-1 rounded">new_message</code>, <code className="bg-muted px-1 rounded">out_of_hours_message</code>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWebhooksDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveWebhooks} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Evolution Dialog */}
      <Dialog open={showEvolutionDialog} onOpenChange={setShowEvolutionDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code2 className="h-5 w-5 text-primary" />
              Configurar Evolution
            </DialogTitle>
            <DialogDescription>
              Configure os dados da Evolution para {selectedAdmin?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>URL Evolution</Label>
              <Input
                placeholder="https://sua-evolution.exemplo.com"
                value={evolutionData.evolution_url}
                onChange={(e) => setEvolutionData({ ...evolutionData, evolution_url: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                URL base da Evolution (ex: https://evolution.seudominio.com)
              </p>
            </div>

            <div className="space-y-2">
              <Label>API Key Evolution</Label>
              <div className="relative">
                <Input
                  type={showEvolutionApiKey ? 'text' : 'password'}
                  placeholder="Chave da API"
                  value={evolutionData.evolution_api_key}
                  onChange={(e) => setEvolutionData({ ...evolutionData, evolution_api_key: e.target.value })}
                />
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="absolute right-0 top-0 h-full"
                  onClick={() => setShowEvolutionApiKey(!showEvolutionApiKey)}
                >
                  {showEvolutionApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Mantida como senha na interface (não aparece em texto aberto).
              </p>
            </div>

            <div className="space-y-2">
              <Label>Webhook Evolution</Label>
              <Input
                placeholder="https://sua-api.com/webhook/evolution"
                value={evolutionData.evolution_webhook}
                onChange={(e) => setEvolutionData({ ...evolutionData, evolution_webhook: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Webhook configurado na Evolution para eventos (opcional).
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEvolutionDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEvolution} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trial Dialog */}
      <Dialog open={trialDialogOpen} onOpenChange={setTrialDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ativar Período de Teste</DialogTitle>
            <DialogDescription>
              Defina o período de teste para {selectedAdminForTrial?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Dias de teste</Label>
              <Input
                type="number"
                min="1"
                max="90"
                value={trialDays}
                onChange={(e) => setTrialDays(e.target.value)}
                placeholder="7"
              />
              <p className="text-xs text-muted-foreground">
                O período de teste permite acesso temporário à plataforma (1-90 dias)
              </p>
            </div>
            {selectedAdminForTrial && (
              <div className="p-3 bg-secondary/50 rounded-lg text-sm">
                <p><strong>Admin:</strong> {selectedAdminForTrial.name}</p>
                <p><strong>Email:</strong> {selectedAdminForTrial.email}</p>
                <p><strong>Expira em:</strong> {trialDays} dias a partir de hoje</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTrialDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleActivateTrial} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Ativar Teste
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* API Dialog */}
      <Dialog open={showApiDialog} onOpenChange={setShowApiDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-primary" />
              Link de API
            </DialogTitle>
            <DialogDescription>
              API pública para consultar informações de {selectedAdmin?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* API URL */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                URL da API
                <Badge variant="secondary" className="text-xs">GET</Badge>
              </Label>
              <p className="text-xs text-muted-foreground">
                Use esta URL para consultar informações do administrador, instâncias, configurações e estatísticas
              </p>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={selectedAdmin ? getPublicApiUrl(selectedAdmin) : ''}
                  className="font-mono text-sm bg-muted"
                />
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => copyToClipboard(getPublicApiUrl(selectedAdmin), 'api_url')}
                  title="Copiar URL"
                >
                  {copiedField === 'api_url' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => window.open(getPublicApiUrl(selectedAdmin), '_blank')}
                  title="Abrir em nova aba"
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <Separator />

            {/* Response Info */}
            <div className="space-y-3">
              <Label>Informações retornadas pela API:</Label>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="p-2 bg-secondary/30 rounded">
                  <code className="text-primary">admin</code>
                  <p className="text-xs text-muted-foreground">ID, nome, email, status, tipo de acesso</p>
                </div>
                <div className="p-2 bg-secondary/30 rounded">
                  <code className="text-primary">trial</code>
                  <p className="text-xs text-muted-foreground">Período de teste, dias restantes</p>
                </div>
                <div className="p-2 bg-secondary/30 rounded">
                  <code className="text-primary">business_hours</code>
                  <p className="text-xs text-muted-foreground">Horário de atendimento</p>
                </div>
                <div className="p-2 bg-secondary/30 rounded">
                  <code className="text-primary">closing_message</code>
                  <p className="text-xs text-muted-foreground">Mensagem de encerramento</p>
                </div>
                <div className="p-2 bg-secondary/30 rounded">
                  <code className="text-primary">welcome_message</code>
                  <p className="text-xs text-muted-foreground">Mensagem de boas-vindas</p>
                </div>
                <div className="p-2 bg-secondary/30 rounded">
                  <code className="text-primary">satisfaction_survey</code>
                  <p className="text-xs text-muted-foreground">Pesquisa de satisfação</p>
                </div>
                <div className="p-2 bg-secondary/30 rounded">
                  <code className="text-primary">webhooks</code>
                  <p className="text-xs text-muted-foreground">URLs de webhook configuradas</p>
                </div>
                <div className="p-2 bg-secondary/30 rounded">
                  <code className="text-primary">instances</code>
                  <p className="text-xs text-muted-foreground">Lista de instâncias WhatsApp</p>
                </div>
                <div className="p-2 bg-secondary/30 rounded">
                  <code className="text-primary">integrations</code>
                  <p className="text-xs text-muted-foreground">Integrações configuradas (SGP, etc)</p>
                </div>
                <div className="p-2 bg-secondary/30 rounded">
                  <code className="text-primary">attendants</code>
                  <p className="text-xs text-muted-foreground">Lista de atendentes</p>
                </div>
                <div className="p-2 bg-secondary/30 rounded">
                  <code className="text-primary">quick_replies</code>
                  <p className="text-xs text-muted-foreground">Respostas rápidas</p>
                </div>
                <div className="p-2 bg-secondary/30 rounded">
                  <code className="text-primary">stats</code>
                  <p className="text-xs text-muted-foreground">Contadores e estatísticas</p>
                </div>
              </div>
            </div>

            <Separator />

            {/* Token Info */}
            <div className="space-y-2">
              <Label>Token de acesso</Label>
              <p className="text-xs text-muted-foreground">
                O token abaixo é usado para autenticar requisições à API
              </p>
              <div className="flex gap-2">
                <Input
                  readOnly
                  type="password"
                  value={selectedAdmin?.webhook_token || ''}
                  className="font-mono text-sm bg-muted"
                />
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => copyToClipboard(selectedAdmin?.webhook_token, 'token')}
                  title="Copiar token"
                >
                  {copiedField === 'token' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApiDialog(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeveloperDashboard;
